import React from 'react';
import { Clock } from 'lucide-react';
import { CalendarEvent, Reunion } from '../../types';
import { ActionMenu } from '../shared/ActionMenu';
import { CalendarDayActionMenu } from '../agenda/CalendarDayActionMenu';
import { CalendarHeader } from '../calendar/CalendarHeader';
import { WeekView } from '../calendar/WeekView';
import { DayEventsSection } from '../calendar/DayEventsSection';
import { reunionToCalendarEvent } from '../../lib/adapters';
import { parseDateString } from '../../lib/dateUtils';

interface AgendaViewProps {
  calendar: {
    currentDate: Date;
    calendarViewMode: 'week' | 'month';
    setCalendarViewMode: (mode: 'week' | 'month') => void;
    selectedDate: string;
    setSelectedDate: (date: string) => void;
    monthGrid: (number | null)[][];
    weekDayNames: string[];
    changeMonth: (increment: number) => void;
    isToday: (day: number) => boolean;
    formatDate: (day: number) => string;
  };
  calendarReuniones: Reunion[];
  onActionClick: (actionId: string) => void;
  onDayAction: (actionId: string, day: number) => void;
  onCalendarDayClick: (day: number) => void;
  onEventClick: (event: CalendarEvent) => void;
  isMobile: boolean;
  isTablet: boolean;
}

/**
 * AgendaView - Calendar view for territorial agenda
 *
 * Features:
 * - Month/week toggle (mobile)
 * - Full month calendar grid
 * - Week view with day events panel (mobile)
 * - Event previews in calendar cells
 * - Quick action menus
 */
export function AgendaView({
  calendar,
  calendarReuniones,
  onActionClick,
  onDayAction,
  onCalendarDayClick,
  onEventClick,
  isMobile,
  isTablet,
}: AgendaViewProps) {
  const {
    currentDate,
    calendarViewMode,
    setCalendarViewMode,
    selectedDate,
    setSelectedDate,
    monthGrid,
    weekDayNames,
    changeMonth,
    isToday,
    formatDate,
  } = calendar;

  return (
    <>
      {/* Page Header */}
      <div className="flex-none px-4 md:px-8 py-4 md:py-6 border-b border-[#D1D5DB] bg-white relative shadow-sm">
        {isMobile ? (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#111827]">Agenda Territorial</h2>
              <p className="text-[#6B7280] text-xs mt-0.5">
                {currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            <ActionMenu onActionClick={onActionClick} />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
                Agenda Territorial
              </h2>
              <p className="text-[#6B7280] text-sm mt-1">Planificación y seguimiento de actividades en campo</p>
            </div>
            <ActionMenu onActionClick={onActionClick} />
          </div>
        )}
      </div>

      {/* View Toggle: Week / Month (Mobile Only) */}
      {isMobile && (
        <div className="flex-none px-4 py-3 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setCalendarViewMode('week')}
              className={`flex-1 py-2 px-4 text-sm font-semibold rounded-md transition-all ${
                calendarViewMode === 'week'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Semana
            </button>
            <button
              onClick={() => setCalendarViewMode('month')}
              className={`flex-1 py-2 px-4 text-sm font-semibold rounded-md transition-all ${
                calendarViewMode === 'month'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500'
              }`}
            >
              Mes Completo
            </button>
          </div>
        </div>
      )}

      {/* Calendar Content */}
      <div className={`flex-1 overflow-y-auto custom-scrollbar bg-[#F3F4F6] ${isMobile ? 'p-0' : isTablet ? 'p-4' : 'p-6'}`}>
        {calendarViewMode === 'week' && isMobile ? (
          // Week View Mode (Mobile Only)
          <div className="flex flex-col h-full">
            <WeekView
              currentDate={currentDate}
              selectedDay={parseInt(selectedDate.split('-')[2]) || new Date().getDate()}
              onDayClick={(day) => {
                const formattedDate = formatDate(day);
                setSelectedDate(formattedDate);
              }}
              onPrevWeek={() => {
                const newDate = new Date(currentDate);
                newDate.setDate(newDate.getDate() - 7);
              }}
              onNextWeek={() => {
                const newDate = new Date(currentDate);
                newDate.setDate(newDate.getDate() + 7);
              }}
              isMobile={isMobile}
              eventDays={(() => {
                const daysWithEvents = new Set<number>();
                calendarReuniones.forEach(reunion => {
                  // Use parseDateString to avoid timezone shift issues
                  const { year, month, day } = parseDateString(reunion.date);
                  if (month - 1 === currentDate.getMonth() && year === currentDate.getFullYear()) {
                    daysWithEvents.add(day);
                  }
                });
                return Array.from(daysWithEvents);
              })()}
            />
            <DayEventsSection
              selectedDate={(() => {
                // Use parseDateString to avoid timezone shift issues
                // "2026-02-05" parsed as UTC becomes Feb 4 in negative UTC offsets
                const { year, month, day } = parseDateString(selectedDate);
                return new Date(year, month - 1, day);
              })()}
              events={(() => {
                const calendarEvents = calendarReuniones.map(reunionToCalendarEvent);
                return calendarEvents
                  .filter(e => e.date === selectedDate)
                  .map(e => ({
                    id: e.id || '',
                    title: e.title,
                    time: e.time,
                    location: e.municipality,
                    date: e.date || '',
                  }));
              })()}
              onEventClick={(eventId) => {
                const calendarEvents = calendarReuniones.map(reunionToCalendarEvent);
                const clickedEvent = calendarEvents.find(e => e.id === eventId);
                if (clickedEvent) {
                  onEventClick(clickedEvent);
                }
              }}
              onAddEvent={() => {
                onActionClick('reunion');
              }}
            />
          </div>
        ) : (
          // Month View Mode (Desktop/Tablet + Mobile Optional)
          <div className={`bg-white border border-[#D1D5DB] shadow-sm overflow-hidden flex flex-col min-h-full ${isMobile ? 'rounded-xl' : 'rounded-2xl'}`}>
            <CalendarHeader
              currentDate={currentDate}
              onPrevMonth={() => changeMonth(-1)}
              onNextMonth={() => changeMonth(1)}
              viewMode={calendarViewMode}
              onViewModeChange={setCalendarViewMode}
              isMobile={isMobile}
            />

            {/* Days Header */}
            <div className="flex-none grid grid-cols-7 border-b border-[#D1D5DB] bg-[#F9FAFB]">
              {weekDayNames.map((day, index) => (
                <div
                  key={day}
                  className={`py-2 md:py-3 text-center text-xs font-bold text-[#6B7280] uppercase tracking-wider ${index !== 6 ? 'border-r border-[#D1D5DB]/50' : ''}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Full Month Grid */}
            <div className="grid grid-cols-7 bg-white">
              {monthGrid.map((week, weekIndex) => (
                <React.Fragment key={weekIndex}>
                  {week.map((day, dayIndex) => {
                    const targetDate = day ? formatDate(day) : '';
                    const calendarEvents = calendarReuniones.map(reunionToCalendarEvent);
                    const dayEvents = calendarEvents.filter(e => e.date === targetDate);
                    const todayCheck = day ? isToday(day) : false;

                    const isLastRow = weekIndex === monthGrid.length - 1;
                    const isLastCol = dayIndex === 6;

                    const cellHeight = isMobile ? 'h-[70px]' : isTablet ? 'h-[85px]' : 'h-[100px]';
                    const cellPadding = isMobile ? 'p-2' : isTablet ? 'p-2.5' : 'p-3';

                    return (
                      <div
                        key={dayIndex}
                        onClick={() => day && onCalendarDayClick(day)}
                        className={`
                          ${cellHeight}
                          ${cellPadding}
                          relative group transition-colors flex flex-col cursor-pointer
                          ${!isLastRow ? 'border-b border-[#E5E7EB]' : ''}
                          ${!isLastCol ? 'border-r border-[#E5E7EB]' : ''}
                          ${!day ? 'bg-[#F9FAFB] cursor-default' : 'bg-white hover:bg-[#F9FAFB]'}
                        `}
                      >
                        {day && (
                          <>
                            {calendarViewMode === 'month' ? (
                              // Calendar Mode: Event Previews
                              <>
                                {(() => {
                                  const dayNumberSize = isMobile ? 'w-6 h-6 text-xs' : isTablet ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm';
                                  const maxEventsHeight = isMobile ? 'max-h-[50px]' : isTablet ? 'max-h-[60px]' : 'max-h-[80px]';
                                  const maxEventsToShow = isMobile ? 2 : isTablet ? 2 : 3;
                                  const titleTruncateLength = isMobile ? 12 : isTablet ? 16 : 20;
                                  const eventBadgeClass = isMobile ? 'text-[10px] px-1.5 py-1' : isTablet ? 'text-[10px] px-1.5 py-1' : 'text-xs px-2 py-1.5';

                                  return (
                                    <>
                                      <div className="flex justify-between items-start mb-1 flex-none">
                                        <span className={`
                                          ${dayNumberSize}
                                          flex items-center justify-center rounded-full font-medium transition-all
                                          ${todayCheck
                                            ? 'bg-[#5D1828] text-white shadow-md shadow-[#9E2A2F]/50'
                                            : 'text-[#6B7280] group-hover:text-[#111827] group-hover:bg-[#E5E7EB]'
                                          }`}
                                        >
                                          {day}
                                        </span>

                                        {!isMobile && (
                                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10" onClick={(e) => e.stopPropagation()}>
                                            <CalendarDayActionMenu onAction={(action) => onDayAction(action, day)} />
                                          </div>
                                        )}
                                      </div>

                                      <div className={`space-y-1 overflow-y-auto no-scrollbar ${maxEventsHeight}`}>
                                        {dayEvents.slice(0, maxEventsToShow).map((event, idx) => (
                                          <div
                                            key={idx}
                                            className={`flex items-center gap-1 ${eventBadgeClass} rounded-md border ${event.color} transition-transform hover:scale-[1.02] cursor-pointer shadow-sm`}
                                          >
                                            <span className="flex-shrink-0">{event.emoji || '📅'}</span>
                                            <span className="font-semibold truncate flex-1">
                                              {event.title.length > titleTruncateLength
                                                ? event.title.substring(0, titleTruncateLength) + '...'
                                                : event.title}
                                            </span>
                                            {!isMobile && (
                                              <div className="flex items-center gap-0.5 opacity-75 text-[10px]">
                                                <Clock size={10} />
                                                <span>{event.time}</span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        {dayEvents.length > maxEventsToShow && (
                                          <div className="text-[10px] text-[#6B7280] text-center">
                                            +{dayEvents.length - maxEventsToShow} más
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              // Text Mode: Simplified with Dots
                              <>
                                <div className="flex justify-between items-start flex-none">
                                  <span className={`
                                    ${isMobile ? 'w-6 h-6 text-xs' : isTablet ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm'}
                                    flex items-center justify-center rounded-full font-medium transition-all
                                    ${todayCheck
                                      ? 'bg-[#5D1828] text-white shadow-md shadow-[#9E2A2F]/50'
                                      : 'text-[#6B7280] group-hover:text-[#111827] group-hover:bg-[#E5E7EB]'
                                    }`}
                                  >
                                    {day}
                                  </span>
                                </div>

                                {dayEvents.length > 0 && (
                                  <div className="mt-auto flex gap-1 justify-center pt-1">
                                    {dayEvents.slice(0, 3).map((event, idx) => (
                                      <span
                                        key={idx}
                                        className="w-1.5 h-1.5 rounded-full bg-[#D4A658]"
                                      />
                                    ))}
                                    {dayEvents.length > 3 && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4A658]" />
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
